/*
 * Project system (concept §8): new, save, load, autosave.
 * File type `.scapemaker` — JSON, NO audio. Assets are referenced by source URL
 * and resolved from the IndexedDB cache first, then downloadUrl. On load,
 * missing assets are reported clearly — never a silent half-open.
 */

import { openDB } from 'idb';
import type { AssetRef, Project } from './project';
import { migrate, newProject } from './project';
import { assetStore, cacheKey } from '../audio/assetStore';
import { store } from './store';
import { history } from './history';
import { downloadBlob, safeName } from '../export/download';
import {
  currentHandle,
  ensureWritable,
  isAbort,
  pickSaveFile,
  setCurrentHandle,
  supportsFileSystemAccess,
  writeToHandle,
} from './fileHandle';

const AUTOSAVE_DB = 'scapemaker-autosave';
const AUTOSAVE_STORE = 'snapshots';
const AUTOSAVE_KEY = 'latest';

// --- file save / load ---------------------------------------------------

export function serialiseProject(project: Project): string {
  return JSON.stringify(project, null, 2);
}

/**
 * Save the project. Where the browser supports it, this writes back to the
 * file the student opened or last saved to, so repeated saves update one file
 * instead of accumulating "Soundscape (9).scapemaker" in Downloads.
 *
 * `saveAs` forces the picker. Must be called from a user gesture: the
 * permission prompt needs one, and every caller (button, Cmd+S) has one.
 */
export async function saveProjectToFile(project: Project, opts: { saveAs?: boolean } = {}): Promise<void> {
  const text = serialiseProject(project);
  const suggested = `${safeName(project.name)}.scapemaker`;

  if (supportsFileSystemAccess()) {
    try {
      let handle = opts.saveAs ? null : currentHandle();
      if (handle && !(await ensureWritable(handle))) handle = null; // grant lapsed; re-pick
      if (!handle) handle = await pickSaveFile(suggested);
      if (!handle) throw new Error('no picker');
      await writeToHandle(handle, text);
      setCurrentHandle(handle);
      store.markSaved();
      store.toast('info', `Saved to ${handle.name}.`, 2200);
      return;
    } catch (err) {
      // Cancelling the picker is a decision, not a failure.
      if (isAbort(err)) return;
      // Anything else (no API, a read-only location): fall through to download.
    }
  }

  downloadBlob(new Blob([text], { type: 'application/json' }), suggested);
  store.markSaved();
}

/** Where the next plain save will write, for the top bar to name. */
export function currentFileName(): string | null {
  return currentHandle()?.name ?? null;
}

export interface LoadResult {
  project: Project;
  warnings: string[];
  missingAssets: AssetRef[];
}

export async function loadProjectFromText(text: string): Promise<LoadResult> {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new Error('This file is not valid JSON — it may be corrupt or not a .scapemaker file.');
  }
  const { project, warnings } = migrate(raw);

  // Try to bring every referenced asset back online.
  const missingAssets: AssetRef[] = [];
  const usedIds = new Set<string>();
  for (const t of project.tracks) for (const c of t.clips) usedIds.add(c.assetId);

  for (const id of usedIds) {
    const ref = project.assets[id];
    if (!ref) continue;
    try {
      if (await assetStore.isCached(cacheKey(ref))) {
        await assetStore.acquire(ref, { kind: 'url', url: ref.downloadUrl ?? '' });
      } else if (ref.downloadUrl) {
        await assetStore.acquire(ref, { kind: 'url', url: ref.downloadUrl });
      } else {
        missingAssets.push(ref);
      }
    } catch {
      missingAssets.push(ref);
    }
  }

  return { project, warnings, missingAssets };
}

export function applyLoadedProject(project: Project): void {
  store.setProject(project);
  history.clear();
}

/**
 * Guard for any action that discards the current project (New, Open, or
 * navigating away with unsaved work) — asks first when there is something to
 * lose, exactly once, using the browser's own confirm.
 */
export function confirmDiscardIfDirty(verb: string): boolean {
  if (!store.get().ui.dirty) return true;
  return window.confirm(`You have unsaved changes. ${verb} anyway?`);
}

export function startNewProject(): void {
  if (!confirmDiscardIfDirty('Start a new project')) return;
  store.setProject(newProject());
  history.clear();
  setCurrentHandle(null); // a new project has no file yet
  void clearAutosave();
}

// --- autosave (IndexedDB) --------------------------------------------------

async function autosaveDb() {
  return openDB(AUTOSAVE_DB, 1, {
    upgrade(d) {
      if (!d.objectStoreNames.contains(AUTOSAVE_STORE)) d.createObjectStore(AUTOSAVE_STORE);
    },
  });
}

let autosaveTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleAutosave(): void {
  if (autosaveTimer) clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => void writeAutosave(), 4000);
}

export async function writeAutosave(): Promise<void> {
  try {
    const db = await autosaveDb();
    await db.put(
      AUTOSAVE_STORE,
      { project: store.get().project, savedAt: Date.now() },
      AUTOSAVE_KEY,
    );
  } catch {
    /* storage blocked; nothing we can do */
  }
}

export interface AutosaveSnapshot {
  project: Project;
  savedAt: number;
}

export async function readAutosave(): Promise<AutosaveSnapshot | null> {
  try {
    const db = await autosaveDb();
    const snap = (await db.get(AUTOSAVE_STORE, AUTOSAVE_KEY)) as AutosaveSnapshot | undefined;
    return snap ?? null;
  } catch {
    return null;
  }
}

export async function clearAutosave(): Promise<void> {
  try {
    const db = await autosaveDb();
    await db.delete(AUTOSAVE_STORE, AUTOSAVE_KEY);
  } catch {
    /* ignore */
  }
}
